import React, { useState } from 'react';
import { Header, Icon, Segment, Loader, Form } from 'semantic-ui-react';
import { ButtonLink, generateResolver, Notification, STATUS_TYPES, VALIDATORS, yup } from 'dyl-components';

import contactLocationsActions from 'actions/contact_location';
import { useDispatch, useSelector } from 'react-redux';
import { StringUtils } from 'utils';
import Locations from 'shared/Locations';

import './index.scss';
import { Link } from 'react-router-dom';
import AddLocationsSection from './AddLocationsSection';
import { useFieldArray, useForm } from 'react-hook-form';
import RestrictedView from 'shared/RestrictedView';
import { USER_PERMISSIONS } from 'dyl-components';

const LocationsView = ({
    control,
    mainLocationId,
    existingLocations,
    onOpenAddLocationSection,
    isAddLocationSectionOpen,
    onCloseAddLocationSection,
    isUpdatingLocation,
    id,
    type,
    account,
    location_type,
    isLocationOnEditMode,
    onEditLocation,
    tmpLocation,
    pickedLocationId,
    onConfirmEditLocation,
    onCancelEditLocation,
    isValid = true,
    loading,
    refresh,
    updateLocation,
    watchedLocations
}) => (
    <div className='LocationsView'>
        <Loader active={isUpdatingLocation} />
        <Header as='h4' className='LocationsView__Header' >
            My {StringUtils.capitalize(location_type)} Locations
        </Header>
        <Locations
            control={control}
            mainLocationId={mainLocationId}
            existingLocations={existingLocations}
            isLocationOnEditMode={isLocationOnEditMode}
            isUpdatingLocation={isUpdatingLocation}
            onCancelEditLocation={onCancelEditLocation}
            onConfirmEditLocation={onConfirmEditLocation}
            onEditLocation={onEditLocation}
            pickedLocationId={pickedLocationId}
            tmpLocation={tmpLocation}
            location_type={location_type}
            isValid={isValid}
            updateLocation={updateLocation}
            watchedLocations={watchedLocations}
            type={type}
            account={account}
        />
        <Segment className={`LocationsView__Buttons${isAddLocationSectionOpen ? '' : '--close'}`} basic>
            <Form size='tiny'>
                <div>
                    <RestrictedView type="disabled" userPermission={type === "account" ? USER_PERMISSIONS.ACCOUNT_ADD : USER_PERMISSIONS.CONTACTS_ADD}>
                        <ButtonLink className={"LocationsView__AddLocationButton"} onClick={onOpenAddLocationSection}>
                            <Icon name='plus circle' /> <span> Add additional {location_type} location </span>
                        </ButtonLink>
                    </RestrictedView>
                </div>

                {!isAddLocationSectionOpen &&
                    <div>
                        <Link to={`/${type}/${id}/data`}>
                            <Icon name='external' /> <span>View/edit profile data tab</span>
                        </Link>
                    </div>
                }
            </Form>
        </Segment>
        {isAddLocationSectionOpen &&
            <div className='LocationsView__AddLocationSection'>
                <AddLocationsSection
                    id={id}
                    type={type}
                    loading={loading}
                    refresh={refresh}
                    onCloseAddLocationsSection={onCloseAddLocationSection} 
                    location_type={location_type} 
                />
            </div>
        }
    </div>
);

const LocationsViewContainer = ({ refresh, mainLocationId, id, loading, location_type, type, account }) => {
    const [isAddLocationSectionOpen, setIsAddLocationSectionOpen] = useState(false);
    const [isLocationOnEditMode, setIsLocationOnEditMode] = useState(false);
    const [pickedLocationId, setPickedLocationId] = useState(null);

    const { existingLocations } = useSelector((state) => {
        return {
            existingLocations: state[type][type]?.locations || []
        };
    })

    const dispatch = useDispatch();

    const onUpdateLocation = async (locationToUpdate) => {
        const { id: location_id, ...otherProps } = locationToUpdate;
        try {
            const response = await dispatch(contactLocationsActions.updateContactLocation(id, { ...otherProps }, null, location_id));
            Notification.alert('Successfully updated location!', STATUS_TYPES.SUCCESS);
            return response;
        } catch (e) {
            console.log(e);
            Notification.alert('Failed to update location', STATUS_TYPES.ERROR);
            return Promise.reject(e);
        }
    }

    const { control, formState: { isValid, isDirty }, reset, handleSubmit, watch } = useForm({
        mode: 'onChange',
        defaultValues: {
            locations: existingLocations?.map(location => ({
                id: location.id || 0,
                label: location.label || '',
                street: location.street || '',
                additional_street: location.additional_street || '',
                city: location.city || '',
                state: location.state || '',
                zip: location.zip || '',
                billing: location.billing || false,
                shipping: location.shipping || false,
                main: location.main || false
            })) || []
        },
        resolver: generateResolver({
            locations: yup.array().of(yup.object().shape({
                label: yup.string().required(),
                street: yup.string().maxlength(100),
                additional_street: yup.string().maxlength(12),
                city: yup.string().maxlength(60),
                state: yup.string().required('This field is required'),
                zip: VALIDATORS.US_POSTAL_CODE(),
            }))
        })
    })

    const { fields, update: updateLocation } = useFieldArray({
        control,
        name: 'locations',
        keyName: 'key'
    });

    const [watchedLocations] = watch(['locations']);

    const onEditLocation = (locationId) => {
        if (!isAddLocationSectionOpen) {
            setIsLocationOnEditMode(true);
            setPickedLocationId(locationId);
        }
    }

    const onConfirmEditLocation = async (data) => {
        const locationToUpdate = data.locations.find(location => location.id === pickedLocationId);
        return onUpdateLocation(locationToUpdate).then(() => {
            setIsLocationOnEditMode(false);
            refresh();
        })
    }

    const onCancelEditLocation = () => {
        reset();
        setPickedLocationId(null);
        setIsLocationOnEditMode(false);
    }

    const onOpenAddLocationSection = () => {
        setIsAddLocationSectionOpen(true);
        onCancelEditLocation();
    }

    const onCloseAddLocationSection = () => {
        setIsAddLocationSectionOpen(false);
    }

    return (
        <LocationsView
            control={control}
            mainLocationId={mainLocationId}
            existingLocations={existingLocations}
            isAddLocationSectionOpen={isAddLocationSectionOpen}
            onOpenAddLocationSection={onOpenAddLocationSection}
            onCloseAddLocationSection={onCloseAddLocationSection}
            isUpdatingLocation={loading}
            location_type={location_type}
            onEditLocation={onEditLocation}
            isLocationOnEditMode={isLocationOnEditMode}
            tmpLocation={fields}
            pickedLocationId={pickedLocationId}
            onConfirmEditLocation={handleSubmit(onConfirmEditLocation)}
            onCancelEditLocation={onCancelEditLocation}
            id={id}
            type={type}
            account={account}
            isValid={isValid && isDirty}

            loading={loading}
            refresh={refresh}
            updateLocation={updateLocation}
            watchedLocations={watchedLocations}
        />
    )
}

export default LocationsViewContainer;
